import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import {
  Plus,
  Video,
  Clock,
  CheckCircle,
  AlertCircle,
  Play,
  MoreVertical,
  Trash2,
  Edit,
  FolderOpen,
  TrendingUp,
  FileVideo,
  Sparkles,
  Share2,
  Camera,
  Library,
  LogOut,
  CreditCard,
  User,
  Shield,
  ImageIcon
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft: { color: "status-draft", icon: <Edit className="w-3 h-3" />, label: "Draft" },
  generating: { color: "status-generating", icon: <Clock className="w-3 h-3 animate-spin" />, label: "Generating" },
  completed: { color: "status-completed", icon: <CheckCircle className="w-3 h-3" />, label: "Completed" },
  error: { color: "status-error", icon: <AlertCircle className="w-3 h-3" />, label: "Error" }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({ total_projects: 0, completed_projects: 0, draft_projects: 0, in_progress: 0 });
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchProjects();
    fetchStats();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/projects`, { headers });
      setProjects(response.data);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats", error);
    }
  };

  const deleteProject = async (projectId) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/projects/${projectId}`, { headers });
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success("Project deleted");
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
    toast.success("Logged out successfully");
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent-neon flex items-center justify-center">
              <Play className="w-5 h-5 text-white fill-white" />
            </div>
            <span className="font-heading font-bold text-xl text-text-primary">SelleCast</span>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background-paper border border-border">
                <CreditCard className="w-4 h-4 text-primary" />
                <span className="text-primary font-bold">{user.credits}</span>
                <span className="text-text-muted text-sm">credits</span>
              </div>
            )}
            <Button 
              variant="outline"
              className="border-border"
              onClick={() => navigate("/library")}
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Library
            </Button>
            <Button 
              variant="outline"
              className="border-border"
              onClick={() => navigate("/broadcast")}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Broadcast
            </Button>
            <Button 
              variant="outline"
              className="border-border"
              onClick={() => navigate("/selleverify")}
            >
              <Shield className="w-4 h-4 mr-2" />
              SelleVerify
            </Button>
            <Button 
              className="bg-primary hover:bg-primary-hover text-white"
              onClick={() => navigate("/suite")}
              data-testid="create-new-video"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create
            </Button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-border">
                    <User className="w-4 h-4 mr-2" />
                    {user.username}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-background-paper border-border">
                  <DropdownMenuItem className="text-text-muted text-xs">
                    {user.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-text-primary">
                    Plan: <span className="capitalize ml-1 text-primary">{user.plan}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-accent-error hover:bg-accent-error/10"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                variant="outline"
                className="border-border"
                onClick={() => navigate("/login")}
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: "Create Video", icon: <Sparkles className="w-5 h-5" />, path: "/create", color: "bg-primary" },
            { label: "Record Video", icon: <Camera className="w-5 h-5" />, path: "/record", color: "bg-accent-error" },
            { label: "View Library", icon: <FolderOpen className="w-5 h-5" />, path: "/library", color: "bg-accent-neon" },
            { label: "Broadcast", icon: <Share2 className="w-5 h-5" />, path: "/broadcast", color: "bg-accent-warning" }
          ].map((action, index) => (
            <motion.div
              key={action.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card 
                className="bg-background-paper border-border hover:border-primary/50 transition-all cursor-pointer group"
                onClick={() => navigate(action.path)}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center text-white`}>
                    {action.icon}
                  </div>
                  <span className="font-medium text-text-primary group-hover:text-primary transition-colors">
                    {action.label}
                  </span>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Videos", value: stats.total_projects, icon: <FolderOpen className="w-5 h-5" />, color: "text-primary" },
            { label: "Completed", value: stats.completed_projects, icon: <CheckCircle className="w-5 h-5" />, color: "text-accent-success" },
            { label: "In Progress", value: stats.in_progress, icon: <TrendingUp className="w-5 h-5" />, color: "text-accent-warning" },
            { label: "Drafts", value: stats.draft_projects, icon: <FileVideo className="w-5 h-5" />, color: "text-text-secondary" }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="bg-background-paper border-border">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`${stat.color}`}>{stat.icon}</span>
                  </div>
                  <div className="font-heading font-bold text-2xl text-text-primary mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-text-muted">{stat.label}</div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Projects Section */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-heading font-bold text-xl text-text-primary">Recent Videos</h2>
          <Button 
            variant="outline" 
            className="border-border hover:bg-background-paper"
            onClick={() => navigate("/templates")}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Templates
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="loading-spinner w-8 h-8" />
          </div>
        ) : projects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 rounded-full bg-background-paper flex items-center justify-center mx-auto mb-6">
              <Video className="w-10 h-10 text-text-muted" />
            </div>
            <h3 className="font-heading font-bold text-xl text-text-primary mb-2">No videos yet</h3>
            <p className="text-text-secondary mb-6">Create your first video or record one</p>
            <div className="flex justify-center gap-3">
              <Button 
                className="bg-primary hover:bg-primary-hover text-white"
                onClick={() => navigate("/create")}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Create Video
              </Button>
              <Button 
                variant="outline"
                className="border-border"
                onClick={() => navigate("/record")}
              >
                <Camera className="w-4 h-4 mr-2" />
                Record Video
              </Button>
            </div>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.slice(0, 6).map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className="bg-background-paper border-border hover:border-primary/50 transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(`/library`)}
                  data-testid={`project-card-${project.id}`}
                >
                  <CardContent className="p-0">
                    {/* Thumbnail */}
                    <div className="aspect-video bg-background-subtle relative overflow-hidden rounded-t-lg">
                      {project.video_url ? (
                        <video
                          src={project.video_url.startsWith('http') ? project.video_url : `${process.env.REACT_APP_BACKEND_URL}${project.video_url}`}
                          className="w-full h-full object-cover"
                          muted
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent-neon/10 flex items-center justify-center">
                          <Video className="w-12 h-12 text-text-muted" />
                        </div>
                      )}
                      
                      {/* Status badge */}
                      <div className={`absolute top-3 left-3 status-badge ${statusConfig[project.status]?.color || 'status-draft'}`}>
                        {statusConfig[project.status]?.icon}
                        <span className="ml-1">{statusConfig[project.status]?.label || 'Draft'}</span>
                      </div>

                      {/* Duration */}
                      <div className="absolute bottom-3 right-3 bg-background/80 backdrop-blur-sm rounded px-2 py-1 text-xs text-text-primary font-mono">
                        {formatDuration(project.target_duration)}
                      </div>

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-6 h-6 text-primary fill-primary ml-1" />
                        </div>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-bold text-text-primary truncate group-hover:text-primary transition-colors">
                            {project.title}
                          </h3>
                          <p className="text-sm text-text-muted truncate mt-1">
                            {project.description || "No description"}
                          </p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-text-muted hover:text-text-primary">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-background-paper border-border">
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); navigate(`/studio/${project.id}`); }}
                              className="text-text-primary hover:bg-background-subtle"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); navigate(`/broadcast/${project.id}`); }}
                              className="text-text-primary hover:bg-background-subtle"
                            >
                              <Share2 className="w-4 h-4 mr-2" />
                              Broadcast
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}
                              className="text-accent-error hover:bg-accent-error/10"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="flex items-center gap-3 mt-3 text-xs text-text-muted">
                        <span className="capitalize px-2 py-0.5 rounded bg-background-subtle">
                          {project.video_type}
                        </span>
                        <span>{formatDate(project.created_at)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {projects.length > 6 && (
          <div className="text-center mt-8">
            <Button
              variant="outline"
              className="border-border"
              onClick={() => navigate("/library")}
            >
              View All Videos
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
